import { Component, ErrorHandler, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { SeAnalyticsService } from 'se-analytics';
import { FiscalYear } from '../../models/fiscal-year.model';
import { FiscalYearService } from '../../services/fiscal-year.service';
import { DateFilterSelection, DateFilterValue } from './date-filter-menu/date-filter-menu.component';

@Component({
  selector: 'app-quick-filters',
  standalone: false,
  templateUrl: './quick-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./quick-filters.component.scss'],
})
export class QuickFiltersComponent implements OnInit {
  constructor(
    public seAnalyticsService: SeAnalyticsService,
    public fiscalYearService: FiscalYearService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private errorHandler: ErrorHandler
  ) {}

  @Input() public disableFilter: boolean;
  @Output() public quickFilterChange = new EventEmitter<void>();

  public fiscalYear?: FiscalYear;
  public selectedDateFilter: DateFilterValue | null | undefined;
  public selectedDateRange: DateRange | null = null;
  public selectedStatuses: string[] = [];

  public defaultFilter: QuickFilter = {
    date_value: 'fiscal-year',
    date_range: null,
    status: ['active'],
  };

  private readonly dateFilters: DateFilterValue[] = [
    'fiscal-year',
    'current-year',
    'last-year',
    'current-month',
    'last-month',
    'date-range',
  ];
  private readonly statusFilters = ['active', 'redeemed', 'expired', 'scheduled'];

  public ngOnInit(): void {
    this.initializeFilters();
    this.loadFiscalYear();
  }

  private initializeFilters(): void {
    const dateFilter = this.activatedRoute.snapshot.queryParamMap.get('date_filter');
    const statusFilter = this.activatedRoute.snapshot.queryParamMap.get('status_filter');
    const dateStart = this.activatedRoute.snapshot.queryParamMap.get('date_start');
    const dateEnd = this.activatedRoute.snapshot.queryParamMap.get('date_end');

    this.selectedDateFilter =
      dateFilter === null ? this.defaultFilter.date_value : this.isDateFilterValue(dateFilter) ? dateFilter : null;
    this.selectedDateRange =
      this.selectedDateFilter === 'date-range' && dateStart && dateEnd
        ? { start_date: dateStart, end_date: dateEnd }
        : this.dateRange(this.selectedDateFilter);

    if (this.selectedDateFilter === 'date-range' && !this.selectedDateRange.start_date) {
      this.selectedDateFilter = null;
    }

    this.selectedStatuses =
      statusFilter === null
        ? [...this.defaultFilter.status]
        : statusFilter.split(',').filter((status) => this.statusFilters.includes(status));
  }

  public applyDateFilter(selection: DateFilterSelection): void {
    this.selectedDateFilter = selection.filter;
    this.selectedDateRange = { start_date: selection.startDate, end_date: selection.endDate };
    this.commitFilters();
  }

  public clearDateFilter(): void {
    this.selectedDateFilter = null;
    this.selectedDateRange = null;
    this.commitFilters();
  }

  public applyStatusFilter(statuses: string[]): void {
    this.selectedStatuses = [...statuses];
    this.commitFilters();
  }

  public clearStatusFilter(): void {
    this.selectedStatuses = [];
    this.commitFilters();
  }

  private dateRange(date_value: DateFilterValue | null | undefined): DateRange {
    const today = DateTime.now();
    let startDate: DateTime;
    let endDate: DateTime;

    switch (date_value) {
      case 'fiscal-year':
        if (!this.fiscalYear) {
          return { start_date: null, end_date: null };
        }
        startDate = DateTime.fromJSDate(this.fiscalYearService.fiscalYearStartDate(this.fiscalYear));
        endDate = DateTime.fromJSDate(this.fiscalYearService.fiscalYearEndDate(this.fiscalYear));
        break;
      case 'current-year':
        startDate = today.startOf('year');
        endDate = today.endOf('year');
        break;
      case 'last-year':
        startDate = today.minus({ year: 1 }).startOf('year');
        endDate = today.minus({ year: 1 }).endOf('year');
        break;
      case 'last-month':
        startDate = today.minus({ month: 1 }).startOf('month');
        endDate = today.minus({ month: 1 }).endOf('month');
        break;
      case 'current-month':
        startDate = today.startOf('month');
        endDate = today.endOf('month');
        break;
      default:
        return { start_date: null, end_date: null };
    }

    return {
      start_date: startDate ? startDate.toISODate() : null,
      end_date: endDate ? endDate.toISODate() : null,
    };
  }

  public loadFiscalYear(): void {
    this.fiscalYearService.find().subscribe({
      next: (fiscalYear) => {
        this.fiscalYear = fiscalYear;
        if (this.selectedDateFilter === 'fiscal-year') {
          this.selectedDateRange = this.dateRange(this.selectedDateFilter);
          this.quickFilterChange.emit();
        }
      },
      error: (error: unknown) => this.errorHandler.handleError(error),
    });
  }

  /**
   * Sets the current fiscal year for the component.
   *
   * If a fiscal year is already set, it will be retained; otherwise, the provided `fiscalYear` parameter will be used.
   * Recalculates the date range only when the fiscal-year filter is selected so restored filters remain unchanged.
   *
   * @param fiscalYear - (Optional) The fiscal year to set if one is not already present.
   */
  public setFiscalYear(fiscalYear?: FiscalYear): void {
    this.fiscalYear ??= fiscalYear;
    if (this.selectedDateFilter === undefined) {
      this.selectedDateFilter = this.defaultFilter.date_value;
    }

    if (this.selectedDateFilter === 'fiscal-year') {
      this.selectedDateRange = this.dateRange(this.selectedDateFilter);
    }

    this.quickFilterChange.emit();
  }

  public unset() {
    this.selectedDateFilter = null;
    this.selectedDateRange = null;
    this.selectedStatuses = [];
    this.persistFilters();
    this.quickFilterChange.emit();
  }

  private commitFilters(): void {
    this.applyFilterTracking(this.selectedDateFilter, this.selectedStatuses);
    this.persistFilters();
    this.quickFilterChange.emit();
  }

  private persistFilters(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        date_filter: this.selectedDateFilter || '',
        date_start: this.selectedDateFilter === 'date-range' ? this.selectedDateRange?.start_date || '' : '',
        date_end: this.selectedDateFilter === 'date-range' ? this.selectedDateRange?.end_date || '' : '',
        status_filter: this.selectedStatuses.join(','),
      },
      queryParamsHandling: 'merge',
    });
  }

  private isDateFilterValue(value: string): value is DateFilterValue {
    return this.dateFilters.some((filter) => filter === value);
  }

  private applyFilterTracking(dateFilter, statuses) {
    const filter: any = { count: 0 };
    let action: string;
    if (dateFilter) {
      filter.dateFilter = dateFilter;
      action = `QuickFilters.${filter.dateFilter}.${filter.count}`;
    }
    if (statuses.length > 0) {
      filter.statuses = statuses.join('.');
      filter.count = statuses.length;
      action = `QuickFilters.${filter.count}.${filter.statuses}`;
    }
    if (dateFilter && statuses.length > 0) {
      action = `QuickFilters.${filter.dateFilter}.${filter.count}.${filter.statuses}`;
    }
    this.seAnalyticsService.trackEvent('seEvent', {
      depth1: 'HQ',
      depth2: 'Discounts',
      action,
      event_type: 8,
    });
  }
}

export interface DateRange {
  start_date: string | null;
  end_date: string | null;
}

export interface QuickFilter {
  date_value: DateFilterValue;
  date_range: DateRange | null;
  status: string[];
}
